import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarDays, Clock, Video, MapPin, CheckCircle2, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const { consultationId } = useParams();
  const { t, i18n } = useTranslation();
  const [slot, setSlot] = useState(null);
  const [state, setState] = useState('loading'); // loading | form | unavailable | done
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '' });

  const regionLabel = useCallback(
    (region) =>
      ({
        indonesia: t('contact.indonesiaFull'),
        maldives: t('contact.maldives'),
        vietnam: t('contact.vietnam'),
        cyprus: t('contact.northernCyprus')
      }[region] || region),
    [t]
  );

  useEffect(() => {
    document.title = 'Reserve Your Place | Fundora Global';
    window.scrollTo(0, 0);
    axios
      .get(`${BACKEND_URL}/api/consultation-slots/${consultationId}`)
      .then(({ data }) => {
        setSlot(data);
        setState('form');
      })
      .catch(() => setState('unavailable'));
  }, [consultationId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/registrations`, {
        consultation_id: consultationId,
        ...form,
        language: (i18n.language || 'en').slice(0, 2)
      });
      setState('done');
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('contact.regError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return <div className="min-h-screen bg-[#102131]" data-testid="register-loading" />;
  }

  if (state === 'unavailable') {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-[#102131] text-[#F7FDFF]" data-testid="register-unavailable">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('contact.regUnavailable')}</h1>
          <p className="text-base text-[#F7FDFF]/70 mb-10">{t('contact.regUnavailableDesc')}</p>
          <Link to="/contact">
            <Button data-testid="see-upcoming-btn" className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold">
              {t('contact.regSeeUpcoming')}
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-[#102131] text-[#F7FDFF]" data-testid="thank-you-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-14 h-14 rounded-full bg-[#CDA259]/15 flex items-center justify-center mb-8">
            <CheckCircle2 className="text-[#CDA259]" size={28} />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6">{t('contact.regThanksTitle')}</h1>
          <p className="text-base md:text-lg text-[#F7FDFF]/85 mb-4">
            {t('contact.regThanksBody', { region: regionLabel(slot.region) })}
          </p>
          <p className="text-base font-semibold text-[#CDA259] mb-8">
            {slot.date} · {slot.time} {t('contact.regTimeNote')}
          </p>
          <div className="border-l-2 border-[#CDA259]/50 pl-5 mb-10">
            <p className="text-sm md:text-base text-[#F7FDFF]/75" data-testid="thank-you-email-note">
              {t('contact.regThanksEmail')}
            </p>
          </div>
          <p className="text-base mb-10">{t('contact.regThanksClosing')}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/">
              <Button variant="outline" data-testid="thank-you-home-btn" className="border-[#CDA259]/60 text-[#F7FDFF] hover:bg-[#CDA259]/10">
                <ArrowLeft className="mr-2" size={16} />
                {t('contact.regBackHome')}
              </Button>
            </Link>
            <Link to="/properties">
              <Button data-testid="thank-you-browse-btn" className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold">
                {t('contact.regBrowse')}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 bg-[#102131] text-[#F7FDFF]" data-testid="register-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
        {/* Summary */}
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.25em] text-[#CDA259] mb-4">{t('contact.onlineConsultation')}</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('contact.regTitle')}</h1>
          <p className="text-base text-[#F7FDFF]/70 mb-10">{t('contact.regSubtitle')}</p>

          <div className="rounded-xl border border-[#CDA259]/25 bg-[#F7FDFF]/[0.04] p-6 space-y-5" data-testid="consultation-summary">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F7FDFF]/50">{t('contact.regDetails')}</p>
            <div className="flex items-center gap-3">
              <MapPin size={18} className="text-[#CDA259]" />
              <span className="font-semibold" data-testid="summary-region">{regionLabel(slot.region)}</span>
            </div>
            <div className="flex items-center gap-3">
              <CalendarDays size={18} className="text-[#CDA259]" />
              <span data-testid="summary-date">{slot.date}</span>
            </div>
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-[#CDA259]" />
              <span data-testid="summary-time">{slot.time} <span className="text-[#F7FDFF]/50 text-sm">{t('contact.regTimeNote')}</span></span>
            </div>
            <div className="flex items-center gap-3">
              <Video size={18} className="text-[#CDA259]" />
              <span className="text-sm">{t('contact.regFormatValue')}</span>
            </div>
            <div className="pt-4 border-t border-[#F7FDFF]/10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#F7FDFF]/50 mb-1">{t('contact.regConsultationId')}</p>
              <p className="font-mono text-sm text-[#CDA259]" data-testid="summary-consultation-id">{slot.consultation_id}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <form
            onSubmit={handleSubmit}
            data-testid="registration-form"
            className="bg-[#F7FDFF] text-[#102131] rounded-2xl p-8 md:p-10 space-y-6"
          >
            <div>
              <Label htmlFor="reg-name" className="text-[#102131]">{t('contact.regName')} *</Label>
              <Input
                id="reg-name" name="name" required data-testid="reg-name-input"
                value={form.name} onChange={handleChange}
                placeholder={t('contact.fullNamePlaceholder')}
                className="mt-2 border-gray-300 focus:border-[#CDA259]"
              />
            </div>

            <div>
              <Label htmlFor="reg-email" className="text-[#102131]">{t('contact.regEmail')} *</Label>
              <Input
                id="reg-email" name="email" type="email" required data-testid="reg-email-input"
                value={form.email} onChange={handleChange}
                placeholder={t('contact.emailPlaceholder')}
                className="mt-2 border-gray-300 focus:border-[#CDA259]"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="reg-phone" className="text-[#102131]">{t('contact.regPhone')} *</Label>
                <Input
                  id="reg-phone" name="phone" type="tel" required data-testid="reg-phone-input"
                  value={form.phone} onChange={handleChange}
                  placeholder={t('contact.phonePlaceholder')}
                  className="mt-2 border-gray-300 focus:border-[#CDA259]"
                />
              </div>
              <div>
                <Label htmlFor="reg-country" className="text-[#102131]">{t('contact.regCountry')} *</Label>
                <Input
                  id="reg-country" name="country" required data-testid="reg-country-input"
                  value={form.country} onChange={handleChange}
                  placeholder={t('contact.regCountryPlaceholder')}
                  className="mt-2 border-gray-300 focus:border-[#CDA259]"
                />
              </div>
            </div>

            {error && <p className="text-sm text-red-600" data-testid="reg-error">{error}</p>}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="confirm-registration-btn"
              className="w-full bg-[#102131] text-[#F7FDFF] hover:bg-[#1a3449] font-semibold py-6 text-base tracking-wide transition-all duration-200 hover:scale-[1.02]"
            >
              {submitting ? t('contact.regSubmitting') : t('contact.regConfirm')}
            </Button>

            <p className="flex items-start gap-2 text-xs text-gray-500">
              <ShieldCheck size={14} className="text-[#CDA259] mt-0.5 flex-shrink-0" />
              {t('contact.regPrivacy')}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
